import { useRef, useState } from 'react'
import Bildfehler from '../komponenten/Bildfehler'
import Ladehinweis from '../komponenten/Ladehinweis'
import MenschKarte from '../komponenten/MenschKarte'
import Nachbarn from '../komponenten/Nachbarn'
import Stapel from '../komponenten/Stapel'
import Zusammenlegen from '../komponenten/Zusammenlegen'
import { useBild } from '../lib/bild'
import { datum, punkteSatz, useLaden } from '../lib/laden'
import {
  FOLGE_TEXT,
  FOLGE_WIRKUNG,
  GRUND_TEXT,
  holeMeldungen,
  meldungEntscheiden,
  type Folge,
  type Meldung,
} from '../lib/warteschlangen'

/**
 * Was die Meldung betrifft — abgelesen an dem, was sie benennt, nicht an
 * `kind`. Bei 9 der 10 wartenden Bildmeldungen steht dort kein Bild, sondern
 * ein Ort; „Bild gemeldet" wäre dann schlicht falsch.
 */
function artText(m: Pick<Meldung, 'kind' | 'media_id' | 'reported_user_firebase_uid'>): string {
  if (m.kind === 'media') {
    return m.media_id ? 'Bild gemeldet' : 'Fotos am Ort gemeldet'
  }
  if (m.kind === 'review') {
    return m.reported_user_firebase_uid ? 'Bewertung gemeldet' : 'Bewertung gemeldet (nicht benannt)'
  }
  return 'Ort gemeldet'
}

/**
 * Warteschlange „Meldungen" (§8.5).
 *
 * Entscheiden heißt zweierlei: den Zustand der Meldung setzen **und** die
 * Folge ausführen. Die Folge wird hier ausdrücklich gewählt — sie lässt sich
 * aus dem Grund nicht ableiten.
 */
export default function Meldungen() {
  const [zustand, neuLaden] = useLaden(holeMeldungen)
  // Die gewählte Folge je Meldung; die Aktionen lesen sie beim Auslösen.
  const [wahl, setWahl] = useState<Record<string, Folge>>({})
  const wahlRef = useRef(wahl)
  wahlRef.current = wahl
  // Bei einer Dublette zweierlei, das man nicht verwechseln darf: **welcher
  // Zwilling** gemeint ist (aus der Meldung oder von Hand gewählt) und
  // **welcher Ort bleibt**. Wer meldet, meldet oft den besseren Eintrag —
  // „der gemeldete verliert" wäre eine schlechte Regel.
  const [zwilling, setZwilling] = useState<Record<string, string>>({})
  const zwillingRef = useRef(zwilling)
  zwillingRef.current = zwilling
  const [sieger, setSieger] = useState<Record<string, string>>({})
  const siegerRef = useRef(sieger)
  siegerRef.current = sieger

  if (zustand.art !== 'da') return <Ladehinweis zustand={zustand} />

  const folgeVon = (m: Meldung): Folge =>
    wahlRef.current[m.id] ?? m.folgen[0] ?? 'keine'

  return (
    <Stapel<Meldung>
      titel="Meldungen"
      eintraege={zustand.wert.eintraege}
      gesamt={zustand.wert.gesamt}
      schluessel={(m) => m.id}
      neuLaden={neuLaden}
      // Kein Ablehnungsgrund: Hier wird kein Beitrag abgelehnt, sondern
      // festgestellt, dass eine Meldung nicht zutrifft.
      gruende={[]}
      leerText="Keine Meldung wartet auf eine Entscheidung."
      freigebenText="Meldung trifft zu"
      ablehnenText="Meldung trifft nicht zu"
      zeile={(m) => (
        <>
          <span className="stapel-zeile-haupt">
            {GRUND_TEXT[m.reason] ?? m.reason}
          </span>
          <span className="leise">{artText(m)}</span>
        </>
      )}
      inhalt={(m) => (
        <MeldungAnsicht
          m={m}
          folge={folgeVon(m)}
          setFolge={(f) => setWahl((w) => ({ ...w, [m.id]: f }))}
          zwilling={zwilling[m.id] ?? m.related_place_id ?? null}
          setZwilling={(id) => setZwilling((z) => ({ ...z, [m.id]: id }))}
          sieger={sieger[m.id] ?? null}
          setSieger={(id) => setSieger((s) => ({ ...s, [m.id]: id }))}
        />
      )}
      aktion={{
        freigeben: (m) =>
          meldungEntscheiden(
            m.id,
            'approved',
            folgeVon(m),
            siegerRef.current[m.id] ?? null,
            zwillingRef.current[m.id] ?? m.related_place_id ?? null,
          ),
        ablehnen: (m) =>
          meldungEntscheiden(
            m.id,
            'rejected',
            folgeVon(m),
            siegerRef.current[m.id] ?? null,
            zwillingRef.current[m.id] ?? m.related_place_id ?? null,
          ),
        bericht: (m, ergebnis, art) => {
          const name =
            m.mensch?.username ??
            m.mensch?.display_name ??
            (m.mensch ? `Anonym · ${m.mensch.firebase_uid.slice(0, 6)}` : 'niemanden')
          const was =
            art === 'frei' ? 'Meldung trifft zu' : 'Meldung trifft nicht zu'
          // Beim Zusammenlegen sagt die Datenbank, was wirklich umgezogen
          // ist — das ist mehr wert als der Name der Folge.
          const umzug = ergebnis['umzug'] as
            | { fotos: number; bewertungen: number; merkmale: number; bewertungen_doppelt_entfernt: number }
            | undefined
          const folge = umzug
            ? `zusammengelegt: ${umzug.fotos} Fotos, ${umzug.bewertungen} Bewertungen, ` +
              `${umzug.merkmale} Merkmale übernommen` +
              (umzug.bewertungen_doppelt_entfernt > 0
                ? `, ${umzug.bewertungen_doppelt_entfernt} doppelte entfernt`
                : '')
            : (FOLGE_TEXT[ergebnis['folge'] as Folge] ?? 'nichts weiter')
          const zeilen = Number(ergebnis['protokollzeilen'] ?? 1)
          const protokoll = zeilen === 2 ? '2 Zeilen im Protokoll' : '1 Zeile im Protokoll'
          return `${was} · ${folge} · ${protokoll} · ${punkteSatz(ergebnis.punkte.differenz, name)}`
        },
      }}
    />
  )
}

function MeldungAnsicht({
  m,
  folge,
  setFolge,
  zwilling,
  setZwilling,
  sieger,
  setSieger,
}: {
  m: Meldung
  folge: Folge
  setFolge: (f: Folge) => void
  zwilling: string | null
  setZwilling: (ortId: string) => void
  sieger: string | null
  setSieger: (ortId: string) => void
}) {
  const waehltSieger = folge === 'dublette_zusammenlegen'
  return (
    <div className="ansicht">
      <div className="karte">
        <p className="marke">{artText(m)}</p>
        <h2>{GRUND_TEXT[m.reason] ?? m.reason}</h2>
        {m.note && <p className="bewertung-text">{m.note}</p>}
        <p className="leise ansicht-zeile">
          Gemeldet: {datum(m.created_at)}
          {m.melder && ` · von ${m.melder.slice(0, 6)}…`}
        </p>
      </div>

      {m.kind === 'media' && m.media_id && <GemeldetesBild m={m} />}
      {m.kind === 'review' && m.reported_user_firebase_uid && <GemeldeteBewertung m={m} />}
      {(m.kind === 'place' || (m.kind === 'media' && !m.media_id)) && (
        <GemeldeterOrt m={m} />
      )}

      {m.place_id &&
        Number.isFinite(m.ort_lat) &&
        Number.isFinite(m.ort_lon) && (
        <Nachbarn
          lat={m.ort_lat as number}
          lon={m.ort_lon as number}
          ausser={m.place_id}
          ueberschrift={
            waehltSieger && !zwilling
              ? 'Welcher Ort ist der Zwilling?'
              : m.reason === 'duplicate'
                ? 'Kommt einer davon als Zwilling in Frage?'
                : 'Was sonst noch im Umkreis von 150 m liegt'
          }
          leerText={
            m.reason === 'duplicate'
              ? 'Kein weiterer Ort im Umkreis von 150 m. Dann ist es vermutlich keine Dublette.'
              : 'Kein weiterer Ort im Umkreis von 150 m.'
          }
          {...(waehltSieger && !zwilling
            ? { auswahl: { gewaehlt: zwilling, setzen: setZwilling } }
            : {})}
          zusatz={
            m.related_place_id
              ? {
                  id: m.related_place_id,
                  name: m.zwilling_name,
                  street: m.zwilling_strasse,
                  city: m.zwilling_stadt,
                  status: 'published',
                  meter: Number.NaN,
                }
              : null
          }
        />
      )}

      {waehltSieger && m.place_id && zwilling && (
        <Zusammenlegen
          gemeldeterOrt={m.place_id}
          zwilling={zwilling}
          sieger={sieger}
          setzeSieger={setSieger}
        />
      )}

      <div className="karte">
        <h3>
          Was mit {m.kind === 'media' && m.media_id
            ? 'dem Bild'
            : m.kind === 'review' && m.reported_user_firebase_uid
              ? 'der Bewertung'
              : 'dem Ort'}{' '}
          passiert
        </h3>

        {m.ziel_unvollstaendig ? (
          <p className="meldung meldung-warnung">
            {m.kind === 'media' && !m.media_id
              ? 'Diese Meldung sagt „die Fotos hier sind veraltet", benennt aber ' +
                'kein einzelnes Bild — so kamen sie aus der 1.0. Ausführen lässt ' +
                'sich daran nichts; einzelne Bilder entscheidest du in der ' +
                'Warteschlange „Fotos".'
              : 'Diese Meldung benennt ihr Ziel nicht. Du kannst nur festhalten, ' +
                'ob sie zutrifft.'}
          </p>
        ) : (
          <p className="leise">
            Zwei Entscheidungen: Die Knöpfe unten urteilen über die{' '}
            <strong>Meldung</strong>, diese Auswahl über den{' '}
            <strong>Beitrag</strong>. Beides wird zusammen gesetzt, in einer
            Transaktion.
          </p>
        )}
        <div className="folgen" role="radiogroup" aria-label="Folge der Entscheidung">
          {m.folgen.map((f) => (
            <label key={f} className={f === folge ? 'folge aktiv' : 'folge'}>
              <input
                type="radio"
                name={`folge-${m.id}`}
                checked={f === folge}
                onChange={() => setFolge(f)}
              />
              <span className="folge-text">
                <span>{FOLGE_TEXT[f]}</span>
                <span className="leise folge-wirkung">{FOLGE_WIRKUNG[f]}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {(m.reported_user_firebase_uid ?? m.bild_hochlader) && (
        <div className="karte">
          <MenschKarte
            mensch={m.mensch}
            uid={m.reported_user_firebase_uid ?? m.bild_hochlader}
          />
        </div>
      )}
    </div>
  )
}

function GemeldetesBild({ m }: { m: Meldung }) {
  const bild = useBild(m.bild_pfad)
  // Ein Trigger verbirgt Bilder mit 'persons_visible' oder 'offensive' sofort,
  // noch bevor ein Mensch sie gesehen hat (§7.2).
  const vorsorglich = m.bild_status === 'hidden'

  return (
    <div className="karte bild-karte">
      {vorsorglich && (
        <p className="meldung meldung-warnung">
          Wegen der Meldung vorsorglich verborgen. Die Punkte bleiben, solange
          das so ist — erst eine Ablehnung nimmt sie.
        </p>
      )}
      {bild.art === 'laedt' && <p className="gedaempft">Bild lädt …</p>}
      {bild.art === 'fehler' && <Bildfehler zustand={bild} />}
      {bild.art === 'da' && (
        <img className="bild-gross" src={bild.adresse} alt="Gemeldetes Bild" />
      )}
      <p className="leise zahl bild-pfad">{m.bild_pfad}</p>
      <p className="leise">Zustand: {m.bild_status ?? '—'}</p>
    </div>
  )
}

function GemeldeteBewertung({ m }: { m: Meldung }) {
  return (
    <div className="karte">
      <h3>{m.bewertung_titel ?? 'Bewertung ohne Titel'}</h3>
      <p className="gedaempft zahl">
        {m.bewertung_rating !== null ? `${m.bewertung_rating.toFixed(1)} ★` : '—'}
      </p>
      <p className="bewertung-text">{m.bewertung_text ?? '(kein Text)'}</p>
      <p className="leise ansicht-zeile">
        Zeile: {m.bewertung_status ?? '—'} · Text: {m.bewertung_text_status ?? '—'}
      </p>
      <p className="leise">
        Am Ort: {m.ort_name ?? '—'}
        {m.ort_stadt ? `, ${m.ort_stadt}` : ''}
      </p>
    </div>
  )
}

function GemeldeterOrt({ m }: { m: Meldung }) {
  const dublette = m.reason === 'duplicate'

  return (
    <div className={dublette ? 'orte-nebeneinander' : undefined}>
      <div className="karte">
        <h3>{m.ort_name ?? 'Ort ohne Namen'}</h3>
        <p className="gedaempft">
          {[m.ort_strasse, m.ort_stadt].filter(Boolean).join(', ') ||
            'keine Adresse hinterlegt'}
        </p>
        <p className="leise">Zustand: {m.ort_status ?? '—'}</p>
      </div>

      {dublette && m.related_place_id && (
        <div className="karte">
          <h3>{m.zwilling_name ?? 'Zwilling ohne Namen'}</h3>
          <p className="gedaempft">
            {[m.zwilling_strasse, m.zwilling_stadt].filter(Boolean).join(', ') ||
              'keine Adresse hinterlegt'}
          </p>
          <p className="leise">ausdrücklich als Dublette benannt</p>
        </div>
      )}

      {dublette && !m.related_place_id && (
        <div className="karte">
          <p className="gedaempft">
            Diese Meldung benennt keinen Zwilling — bei Bestandsmeldungen aus der
            1.0 ist das die Regel. Was in der Nähe liegt, steht unten.
          </p>
        </div>
      )}
    </div>
  )
}
